import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {LimitedPartner, Subscriber, useFunds} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface SubscriberEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  subscriber?: Subscriber;
  allLimitedPartners?: Array<LimitedPartner>;
}

export function SubscriberEditor(props: SubscriberEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const funds = useFunds();

  const [validating, setValidating] = useState(false);

  const collectionName = 'subscribers';
  const saveFirestoreObject = useMutation(
    (firestoreObject: Subscriber) => {
      // clean out properties that might be undefined
      if (firestoreObject.subscriberInvestmentDate === undefined) {
        delete firestoreObject.subscriberInvestmentDate;
      }
      // clean out properties that might be undefined
      if (firestoreObject.subscriberDivestmentDate === undefined) {
        delete firestoreObject.subscriberDivestmentDate;
      }
      if (firestoreObject.subscriberNotes === undefined) {
        delete firestoreObject.subscriberNotes;
      }

      if (props.subscriber === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.subscriber?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let subscriber: {[key: string]: FormikStoreType} = {
    subscriberId: Date.now(),
    fundId: undefined,
    partnerNumber: undefined,
    subscriberName: undefined,
    subscriberInvestmentDate: undefined,
    subscriberDivestmentDate: undefined,
    subscriberNotes: '',
    subscriberUnits: undefined,
    limitedPartnerIds: [],
  };

  if (props.subscriber !== undefined) {
    subscriber.subscriberId = props.subscriber.subscriberId;
    subscriber.fundId = props.subscriber.fundId;
    subscriber.partnerNumber = props.subscriber.partnerNumber;
    subscriber.subscriberName = props.subscriber.subscriberName;
    subscriber.subscriberInvestmentDate = props.subscriber
      .subscriberInvestmentDate
      ? (
          props.subscriber
            .subscriberInvestmentDate as FirebaseFirestoreTypes.Timestamp
        ).toDate()
      : undefined;
    subscriber.subscriberDivestmentDate = props.subscriber
      .subscriberDivestmentDate
      ? (
          props.subscriber
            .subscriberDivestmentDate as FirebaseFirestoreTypes.Timestamp
        ).toDate()
      : undefined;
    subscriber.subscriberNotes = props.subscriber.subscriberNotes;
    subscriber.subscriberUnits = props.subscriber.subscriberUnits;
    subscriber.limitedPartnerIds = props.subscriber.limitedPartnerIds
      ? [...props.subscriber.limitedPartnerIds]
      : [];
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.subscriber ? 'Edit' : 'Create'} Subscriber`,
              firestoreObject: subscriber,
              fieldMetadata: [
                {
                  key: 'subscriberId',
                  name: 'Subscriber ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'SubscriberId ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'partnerNumber',
                  name: 'Partner Number',
                  type: FormikStoreTypes.number,
                  required: true,
                  errorText: 'Partner Number is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'subscriberName',
                  name: 'Subscriber Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Subscriber Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'subscriberInvestmentDate',
                  name: 'Investment Date',
                  type: FormikStoreTypes.date,
                  required: false,
                  datesUTC: true,
                  errorText: 'Please select a date',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'subscriberDivestmentDate',
                  name: 'Divestment Date',
                  type: FormikStoreTypes.date,
                  required: false,
                  datesUTC: true,
                  errorText: 'Please select a date',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'limitedPartnerIds',
                  name: 'Limited Partners',
                  type: FormikStoreTypes.arraySelect,
                  selectItems: {
                    documents: props.allLimitedPartners ?? [],
                    items:
                      props.allLimitedPartners?.map(
                        limitedPartner => limitedPartner.ref,
                      ) ?? [],
                    labelExtractor: limitedPartner =>
                      limitedPartner?.limitedPartnerName,
                  },
                  required: true,
                  errorText:
                    'Subscribers must have Limited Partners. Add them first if necessary',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'subscriberUnits',
                  name: 'Subscriber Units',
                  type: FormikStoreTypes.number,
                  required: true,
                  errorText: 'Subscriber Units is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'fundId',
                  name: 'Fund',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText: 'Fund is a required field',
                  selectItems: {
                    documents: funds,
                    items: funds?.map(fund => fund.ref) ?? [],
                    labelExtractor: fund => fund?.fundCode,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'subscriberNotes',
                  name: 'Subscriber Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: 'Subscriber Notes is a required field',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as Subscriber,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
